/**
 * HUD number formatting. The sim is SI throughout (AD-007c); the instruments
 * are imperial, because the original's are — "40 mph", "20,000 feet" and a
 * three-digit compass heading are the units the manual and the game's own
 * messages speak in. All conversion happens here, at the UI boundary.
 */

import {
  mpsToMph,
  radiansToDegrees,
  type FlightModelTier,
  type GearState,
  type RealismSettings,
} from "@red-opus/engine";

/** Indicated airspeed in whole mph. */
export function formatAirspeedMph(airspeedMps: number): string {
  return String(Math.max(0, Math.round(mpsToMph(airspeedMps))));
}

/** Altitude in feet, rounded to 10 ft and thousands-separated, like an altimeter's readout. */
export function formatAltitudeFt(altitudeFt: number): string {
  const rounded = Math.round(Math.max(0, altitudeFt) / 10) * 10;
  return rounded.toLocaleString("en-US");
}

/** Compass heading in degrees, 0 = north, wrapped into [0, 360). */
export function headingDegrees(headingRad: number): number {
  const degrees = radiansToDegrees(headingRad) % 360;
  return degrees < 0 ? degrees + 360 : degrees;
}

/** Three-digit compass heading, "000" through "359" — 360 wraps to "000". */
export function formatHeading(headingRad: number): string {
  return String(Math.round(headingDegrees(headingRad)) % 360).padStart(3, "0");
}

const CARDINALS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

export function headingCardinal(headingRad: number): string {
  const sector = Math.round(headingDegrees(headingRad) / 45) % CARDINALS.length;
  return CARDINALS[sector] ?? "N";
}

/** A 0..1 fraction as a whole percent. */
export function formatPercent(fraction: number): string {
  return `${Math.round(Math.min(Math.max(fraction, 0), 1) * 100)}%`;
}

export function formatRounds(rounds: number): string {
  return String(Math.max(0, Math.floor(rounds)));
}

/**
 * What the wheels are doing, while they are down.
 *
 * Port-side wording — the `RB.EXE` string dump has no ground callouts, because
 * none of the captured flights ever took off or landed. It is the only thing
 * left in the HUD's centre-top slot besides a stall: the attitude callout that
 * used to live there (`CLIMBING.` / `DESCENDING.` / `FLYING LEVEL.`, verbatim
 * from the dump) was removed at the user's request on 2026-08-26 — see
 * `hud-snapshot.ts`'s `flightCallout`, which composes this slot, and
 * `docs/engineering/hud-chrome.md` for why the original's own convention did
 * not settle it.
 */
export function groundCallout(gear: GearState): string {
  if (gear.collapsed) return "UNDERCARRIAGE GONE.";
  return gear.stopped ? "STOPPED." : "ON THE GROUND.";
}

const REALISM_LABELS: readonly (readonly [keyof RealismSettings, string])[] = [
  ["aircraftMayBeDamaged", "DAMAGE"],
  ["gunJams", "JAMS"],
  ["blackouts", "BLACKOUTS"],
  ["carburetorFreezes", "CARB"],
  ["limitedFuel", "FUEL"],
  ["limitedAmmunition", "AMMO"],
  ["midairCollisions", "COLLISIONS"],
];

/**
 * The Realism Panel state as a compact chip: the FLIGHT MODEL tier first,
 * then the short name of every toggle that is on (the panel's own labels,
 * abbreviated — `docs/game/flight-model.md` §9 has the full strings).
 */
export function realismSummary(tier: FlightModelTier, realism: RealismSettings): readonly string[] {
  return [tier.toUpperCase(), ...REALISM_LABELS.filter(([key]) => realism[key]).map(([, label]) => label)];
}

/**
 * How long the realism chips stay up at the start of a sortie (user request,
 * 2026-08-26: *"hide the realism pills by default"*).
 *
 * They are hidden rather than deleted because they answer a real question —
 * *what am I flying under?* — and they are shown at the start rather than
 * behind a key because that is the only moment the answer can change: the
 * Realism Panel is read **once per sortie** (`routes/Fly.tsx` takes
 * `currentRealism` at scene creation), so a standing display of it is a
 * constant, and a constant does not need a permanent seventh of the HUD. The
 * permanent copy is the Realism Panel itself, one keypress away at
 * PREFERENCES (`O`) → REALISM — which is the original's own screen and already
 * carries every one of these settings in full words.
 */
export const REALISM_CHIP_SECONDS = 8;

/**
 * Whether the realism chips are still up, as a function of **sim time** rather
 * than of wall clock: a flight paused on the runway is not spending its
 * announcement, and a sortie flown again starts a fresh one because the sim
 * starts at zero. No timer, no signal, nothing to clean up.
 */
export function realismChipsVisible(timeS: number): boolean {
  return timeS < REALISM_CHIP_SECONDS;
}
